import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface LicenseCardProps {
  /** Entity/company name */
  entity: string;
  /** Registration number */
  regNo: string;
  /** Array of license strings (e.g., "FAIS: 665") */
  licenses: string[];
  /** Optional tooltip map for license abbreviations */
  tooltips?: Record<string, string>;
  /** Additional className */
  className?: string;
}

/**
 * component-card-license
 * 
 * A compact card displaying entity licensing information.
 * 
 * Tokens applied:
 * - Dimensions: w-[280px] h-[160px]
 * - Surface: bg-muted/50 (subtle on white/platinum sections)
 * - Radius: rounded-3xl
 * - Padding: p-5
 * - Layout: flex flex-col justify-between
 * - Entity name: text-base font-bold text-carbon
 * - Tags: px-3 py-1 rounded-full bg-white text-carbon text-xs
 */
export function LicenseCard({
  entity,
  regNo,
  licenses,
  tooltips = {},
  className = "",
}: LicenseCardProps) {
  return (
    <div 
      className={cn(
        "w-[280px] h-[160px] p-5 rounded-3xl bg-graphite flex flex-col justify-between flex-shrink-0",
        className
      )}
    >
      {/* atom-text-license-entity: text-base font-bold text-platinum */}
      <p className="text-base font-bold text-platinum">
        {entity}
      </p>
      
      {/* molecule-tag-group-license */}
      <div className="flex flex-wrap gap-1.5">
        {/* Registration tag - always first */}
        <span className="inline-block px-3 py-1 rounded-full bg-black text-platinum text-xs">
          Reg: {regNo}
        </span>
        
        {/* License tags with optional tooltips */}
        {licenses.map((license) => {
          const tooltipKey = Object.keys(tooltips).find(key => license.includes(key));
          const tooltip = tooltipKey ? tooltips[tooltipKey] : null;
          
          if (tooltip) {
            return (
              <Tooltip key={license}>
                <TooltipTrigger asChild>
                  <span className="inline-block px-3 py-1 rounded-full bg-black text-platinum text-xs cursor-help transition-colors hover:bg-black/80">
                    {license}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            );
          }
          
          return (
            <span 
              key={license}
              className="inline-block px-3 py-1 rounded-full bg-black text-platinum text-xs"
            >
              {license}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default LicenseCard;
